import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import posthog from "posthog-js";
import { registerAppVersionTelemetry } from "@/lib/appVersion";
import { registerDeviceCapabilityTelemetry } from "@/lib/deviceCapabilityTelemetry";

/**
 * Hard-reset recovery: when retryLazy redirects with ?hard_reset=1 (after a
 * chunk failed to load on a stale client), purge service-worker registrations
 * and Cache Storage, then strip the param from the URL so the loop terminates.
 *
 * Guarded by sessionStorage so a recovery cycle runs at most once per tab —
 * preventing the grey/flicker reload loop reported in production.
 */
async function handleHardResetRecovery(): Promise<boolean> {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("hard_reset")) return false;

  const RECOVERY_KEY = "__behtari_chunk_recovery";
  const alreadyTried = sessionStorage.getItem(RECOVERY_KEY) === "1";

  // Always remove the query param so subsequent navigations are clean.
  url.searchParams.delete("hard_reset");
  const cleanUrl = url.pathname + (url.search ? url.search : "") + url.hash;

  if (alreadyTried) {
    // We've already cleared caches once — don't loop. Just clean the URL.
    window.history.replaceState({}, "", cleanUrl);
    return false;
  }

  sessionStorage.setItem(RECOVERY_KEY, "1");

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    // best-effort; fall through to clean URL replace
  }

  window.history.replaceState({}, "", cleanUrl);
  return false;
}

// PostHog: production-only monitoring. Key from VITE_POSTHOG_KEY env var.
// Never initializes in dev or without a key (safe no-op).
if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      // Targeted masking, matching the project-level replay config set
      // 2026-07-25. maskAllInputs:true here was overriding it, so nothing on the
      // project side could ever take effect. Sensitive fields stay masked via
      // maskInputOptions + the selector list below; ordinary text stays visible
      // so replays are actually usable for debugging low-literacy user flows.
      maskAllInputs: false,
      // Per-input-type masking: credentials, phone numbers, emails and numeric
      // (money/OTP) fields never reach the recorder. Free-text inputs stay
      // visible — that is the point of targeted masking.
      maskInputOptions: {
        password: true,
        tel: true,
        email: true,
        number: true,
      },
      // CNIC and any other field explicitly opted in. CNICInput carries
      // data-sensitive + ph-no-capture; `ph-mask` is rrweb's default mask class.
      maskTextSelector: '[data-sensitive], .ph-no-capture',
    },
    // before_send: filters ONLY the benign "ResizeObserver loop completed
    // with undelivered notifications" browser message (fired via
    // window.onerror when ResizeObserver callbacks can't all complete within
    // one animation frame — common with Radix/popper-based components and
    // charts, never a real malfunction, never shown to the user). Does NOT
    // touch $current_url or any other property on any other event — the
    // previous before_send hook deleted $current_url from EVERY event, which
    // broke authorized-URL filtering, URL-based error filtering and path
    // analysis for no privacy benefit (Behtari paths carry no PII), and was
    // removed for exactly that reason. Keep this hook scoped to this one
    // exception pattern only.
    before_send: (event) => {
      if (event && event.event === '$exception') {
        const props: Record<string, unknown> = event.properties || {};
        const exceptionList = props.$exception_list;
        const firstListedMessage =
          Array.isArray(exceptionList) && exceptionList.length > 0
            ? (exceptionList[0] as { value?: unknown })?.value
            : undefined;
        const message =
          (typeof firstListedMessage === 'string' && firstListedMessage) ||
          (typeof props.$exception_message === 'string' && props.$exception_message) ||
          '';
        if (message.includes('ResizeObserver loop')) {
          return null;
        }
      }
      return event;
    },
  });

  // Stamp every subsequent event (including $exception) with the build that
  // produced it. Adds NO capture call site — it registers super properties on
  // the events the existing sites already send, which is what §20 restricts.
  // Without this, a native exception is not attributable to an APK version at
  // all: the Capacitor WebView reports $lib "web" from https://localhost
  // regardless of which build it is running inside.
  registerAppVersionTelemetry();

  // Stamp every subsequent event with what this device can actually do, and
  // with what the touch-target CSS actually resolved to on it. Same super-
  // property mechanism as above — no new capture call site. This is what makes
  // a future $dead_click sliceable by "did (pointer: coarse) match here?",
  // which is the leading untested hypothesis for the native-vs-web dead-click
  // gap and is not recoverable from already-stored events.
  registerDeviceCapabilityTelemetry();
}

handleHardResetRecovery().finally(() => {
  // Mark a successful boot so the next load can clear the recovery flag.
  // Done after first paint via requestIdleCallback fallback.
  const markBooted = () => {
    try {
      sessionStorage.removeItem("__behtari_chunk_recovery");
    } catch {
      /* noop */
    }
  };
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(markBooted, { timeout: 4000 });
  } else {
    setTimeout(markBooted, 3000);
  }

  // Native shell setup — runs only inside the Capacitor WebView; no-op on web.
  if (Capacitor.isNativePlatform()) {
    document.body.classList.add(`capacitor-${Capacitor.getPlatform()}`);
    StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
    StatusBar.setBackgroundColor({ color: '#3b82f6' }).catch(() => {});
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </HelmetProvider>
    </StrictMode>
  );
});
